import {
  faBook,
  faCircle,
  faCircleCheck,
  faCircleHalfStroke,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components/macro";
import { analytics, PANEL_EVENTS } from "../utils/analytics";

// ─── Animation ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  animation: ${fadeUp} 0.35s ease both;
`;

// ─── Pathway card ─────────────────────────────────────────────────────────────

const PathwayCard = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.accountCard};
  border-top: 2px solid ${({ theme }) => theme.colors.heroPurple};
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.heroPurple};
`;

const CardHeading = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.heading};
  margin: 0;
`;

const TimelineContainer = styled.div`
  position: relative;
  margin-left: 8px;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 5px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: ${({ theme }) => theme.colors.lightGrey};
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  position: relative;
`;

const TimelineDot = styled.span`
  position: relative;
  z-index: 1;
  margin-top: 2px;
  font-size: 12px;
  flex-shrink: 0;
  color: ${({ status, theme }) => {
    if (status === "completed") return theme.colors.heroPurple;
    if (status === "in_progress") return theme.colors.heroOrange;
    return theme.colors.lightGrey;
  }};
`;

const ActivityLabel = styled(Link)`
  font-size: 13px;
  margin: 0;
  text-decoration: none;
  color: ${({ status, theme }) =>
    status === "completed" || status === "in_progress"
      ? theme.colors.heading
      : theme.colors.body};
  &:hover {
    text-decoration: underline;
  }
`;

// ─── AI card ──────────────────────────────────────────────────────────────────
/* 
const AICard = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.accountCard};
  opacity: 0.5;
  filter: grayscale(1);
`;

const AICardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: 8px;
`;

const AICardTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.heading};
  margin: 0;
`;

const ComingSoonBadge = styled.span`
  margin-left: auto;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.altBackground};
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.body};
`;

const AICardBody = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.body};
  margin: 0;
  line-height: 1.55;
`; */

// ─── Resolver (kept for analytics / future wiring) ───────────────────────────

function resolveNextStep(onboardingComplete, inProgressActivity) {
  if (!onboardingComplete) {
    return { stepKey: "onboarding" };
  }
  if (inProgressActivity) {
    return { stepKey: "in_progress" };
  }
  return { stepKey: "professional_vision" };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GPDS-891 — "Your next step" / Vision module
 * Shows a pathway checklist and an AI guidance coming-soon card.
 */
export default function NextStepModule({
  onboardingComplete,
  inProgressActivity,
  activities = [],
}) {
  const step = resolveNextStep(onboardingComplete, inProgressActivity);

  return (
    <Wrapper
      onClick={() =>
        analytics.track(PANEL_EVENTS.NEXT_STEP_CTA_CLICKED, {
          step: step.stepKey,
        })
      }
    >
      {/* Pathway */}
      <PathwayCard>
        <CardHeaderRow>
          <FontAwesomeIcon icon={faBook} size="sm" />
          <CardHeading>Start Here</CardHeading>
        </CardHeaderRow>
        <TimelineContainer>
          <TimelineLine />
          <TimelineList>
            {activities.map((act, i) => (
              <TimelineItem key={act.slug ?? i}>
                <TimelineDot status={act.status}>
                  <FontAwesomeIcon
                    icon={
                      act.status === "completed"
                        ? faCircleCheck
                        : act.status === "in_progress"
                        ? faCircleHalfStroke
                        : faCircle
                    }
                  />
                </TimelineDot>
                <ActivityLabel to={`/lesson/${act.slug}`} status={act.status}>
                  {act.lesson?.replace(/^Start here!\s*/i, "")}
                </ActivityLabel>
              </TimelineItem>
            ))}
          </TimelineList>
        </TimelineContainer>
      </PathwayCard>

      {/* AI Guidance — coming soon */}
      {/* <AICard>
        <AICardHeader>
          <FontAwesomeIcon icon={faMagic} size="sm" />
          <AICardTitle>AI Coach</AICardTitle>
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
        </AICardHeader>
        <AICardBody>
          Personalised AI-powered coaching tips based on your journey.
        </AICardBody>
      </AICard> */}
    </Wrapper>
  );
}
